'use client';

import type { ProductIndexData } from '@eliadmin/core/src/api/products/types/product-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import {
  useProductPriceDisplay,
  WholesalePrices,
} from '@eliadmin/core/src/components/nt/product/product-price';
import { useProductActionErrors } from '@eliadmin/core/src/components/nt/product/use-product-action-errors';
import { stockState } from '@eliadmin/core/src/components/nt/product-detail/hero/use-active-variant';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductImage, getProductUrl, isSellable } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { MatchText, OPTION } from './search-ui';

/**
 * Figma „Price“ of the row: the price before discount (Price/Old, struck) over the current one
 * (Price/M, text/danger when discounted) – the product card rules of `useProductPriceDisplay`. In a
 * narrow list the two sit on one line under the name, the current one first.
 */
function RowPrice({ product }: { product: ProductIndexData }) {
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const price = useProductPriceDisplay(product);
  if (price.priceOnRequest) return null;
  if (price.showMocVocPrices) return <WholesalePrices price={price} />;

  return (
    <p className="flex flex-row-reverse items-baseline justify-end gap-x-2 @[32rem]:flex-col @[32rem]:items-end">
      {price.showPreviousPrice ? (
        <del className="nt-price-old whitespace-nowrap text-[#5D655D]">
          {formatPrice(price.beforeDiscount, currency, currencySettings)}
        </del>
      ) : null}
      <span
        className={cn(
          'nt-price-m whitespace-nowrap',
          price.showPreviousPrice ? 'text-[#B42318]' : 'text-[#0D110D]',
        )}
      >
        {formatPrice(price.current, currency, currencySettings)}
      </span>
    </p>
  );
}

/**
 * Stock of the row with the rules of the product page (`stockState`): the availability name while
 * pieces are in stock, else the availability of a sellable item or the unavailability – green dot
 * and text/brand when it can be ordered, red otherwise.
 */
function RowStock({ product }: { product: ProductIndexData }) {
  const stock = stockState(product);
  const label = stock.inStock ? (product.availability?.name ?? '') : stock.label;
  if (!label) return null;

  return (
    <p className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className={cn(
          'size-1.5 shrink-0 rounded-full',
          stock.sellable ? 'bg-[#127A26]' : 'bg-[#D92D20]',
        )}
      />
      <span
        className={cn(
          'nt-caption-semibold leading-[1.2]',
          stock.sellable ? 'text-[#0E6A20]' : 'text-[#B42318]',
        )}
      >
        {label}
      </span>
    </p>
  );
}

/**
 * Figma „Button Icon“ Primary S (38 px, shopping-cart-simple) on the core add to cart of the product
 * card (variant dialog, cart mutation, analytics, the add-to-cart drawer) – its own variant store
 * and dialog slot like `ProductTileActions`; the core label stays for screen readers.
 */
function RowCartButton({ product }: { product: ProductIndexData }) {
  const { t } = useTranslations();
  const enabled = useGlobalStore(
    state => state.templateSettings.product_teaser?.cart_button?.enabled,
  );
  useProductActionErrors(product);
  if (!enabled) return null;

  return (
    <VariantsProvider state={{ product, selectedQuantity: product.min_quantity ?? 1 }}>
      <DialogProvider>
        <AddToCartButton
          product={product}
          quantity={1}
          sellable={isSellable(product, 1)}
          size="sm"
          iconLeft="shopping-cart-simple"
          aria-label={`${t('text227')}: ${product.name}`}
          className="relative z-[1] w-[38px] px-0 [&>span]:sr-only"
        />
      </DialogProvider>
    </VariantsProvider>
  );
}

/**
 * Figma „Product“ row of 185:14902 (80 px, radius 12, #F5F6F3 on hover / keyboard focus): 60 px
 * photo, the name with the typed words in bold, the stock, the price and the green add to cart. The
 * name link covers the row; the cart button sits above it.
 */
export function SearchProductRow({
  product,
  query,
  onSelect,
}: {
  product: ProductIndexData;
  query: string;
  onSelect: () => void;
}) {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const image = getProductImage(templateSettings, product, []);

  return (
    <li className="relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-[#F5F6F3] has-[a:focus-visible]:bg-[#F5F6F3] @[32rem]:min-h-20 @[32rem]:gap-4">
      <span className="relative size-[60px] shrink-0 overflow-hidden rounded-xl border border-[#E2E5DF] bg-white">
        {image.url ? (
          <Image src={image.url} alt="" fill sizes="60px" className="object-contain" />
        ) : null}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1 @[32rem]:flex-row @[32rem]:items-center @[32rem]:gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Link
            {...OPTION}
            href={getProductUrl(product, [])}
            onClick={onSelect}
            className="nt-body-l line-clamp-2 text-[#0D110D] after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:shadow-focus"
          >
            <MatchText text={product.name} query={query} />
          </Link>
          <RowStock product={product} />
        </div>
        <RowPrice product={product} />
      </div>
      <RowCartButton product={product} />
    </li>
  );
}
